import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux'
import { Badge, ButtonGroup, Button, Input, InputGroup, InputGroupAddon } from 'reactstrap';
import moment from 'moment';
import { Loader, CopyButton, TableButtons, RenounceModal } from '../../../../components';
import { Utils, Tasks } from '../../../../services/';
import { resetTask, loadTask, assignTask, claimTask, renounceTask, changeTaskStatus } from '../../../../redux';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import Config from '../../../../config/Config';

import './TasksListDetails.css';

function TasksListDetails({ tasks, filters, screenings, sectors, shifts, me }) {
    // the server will not serve more than 3500 tasks.
    const TASKS_LIMIT = 3500;

    const dispatch    = useDispatch();
    const { t, i18n } = useTranslation();

    const [taskToRenounce, setTaskToRenounce]           = useState(null);
    const [isOpenRenounceModal, setIsOpenRenounceModal] = useState(false);
    const [editingTasks, setEditingTasks]               = useState([]);
    const [nextCurrentTaskId, setNextCurrentTaskId]     = useState(null);
    const [currentTaskField, setCurrentTaskField]       = useState(null);
    const [icsDescIsVisible, showIcsDesc]               = useState(null);

    const { user }               = useSelector(state => state.auth);
    const { isMobile, isTablet } = useSelector(state => state.ui);
    const isLoadingTask          = useSelector(state => state.tasks.isLoading);
    const currentTask            = useSelector(state => state.tasks.task);

    const isEditing = task => {
        return currentTask?.id === task.id;
    }

    const resetEdition = () => dispatch(resetTask());

    const onTaskClick = (field, task) => {
        setNextCurrentTaskId(task?.id);
        dispatch(loadTask(task.id));
        setCurrentTaskField(field);
    }

    useEffect(() => {
        if (currentTask) {
            const selects = document.querySelectorAll(`#${currentTaskField}-select-${currentTask.id}`);
            if (selects?.length)
                selects[0].showPicker();
        }
    }, [currentTask]);

    const showRenounceModal = (task) => {
        setIsOpenRenounceModal(true);
        setTaskToRenounce(task);
    }

    const closeRenounceModal = () => {
        setIsOpenRenounceModal(false);
        setTaskToRenounce(null);
    }

    const submitRenounceModal = (msg) => {
        dispatch(renounceTask(taskToRenounce.id, msg));
        setIsOpenRenounceModal(false);
        setTaskToRenounce(null);
    }

    const default_lang = Utils.default_lang();

    const sorted_tasks = React.useMemo(() => {
        if (!tasks?.length)
            return [];

        return _.sortBy(tasks, 'start_at').map((t, index) => {
            t.has_started    = moment(t.start_at).isBefore(moment());
            t.is_finished    = moment(t.stop_at).isBefore(moment());
            t.is_editable    = !t.is_finished;
            t.is_assigned    = t.status === Utils.TASK_STATUS_ASSIGNED;
            t.is_claimed     = t.status === Utils.TASK_STATUS_CLAIMED;
            t.is_renounced   = t.status === Utils.TASK_STATUS_RENOUNCED;
            t.is_assignable  = t.is_editable && !t.is_claimed && !t.is_renounced;
            t.is_renouncable = !t.is_finished && t.user_id == me.id && (t.is_assigned || t.is_claimed);
            t.is_claimable   = !t.is_finished &&
                (t.status == Utils.TASK_STATUS_FREE || t.status == Utils.TASK_STATUS_RENOUNCED) && _.filter(me.activities, (a) => a.id == t.activity.id).length == 1;

            return t;
        });
    }, [tasks]);

    const distinctActivities = React.useMemo(() => {
        if (!tasks?.length)
            return [];

        return _.uniq(tasks.map(t => t.activity_id));
    }, [tasks]);

    const keys_map = React.useMemo(() => {
        const keys_map = {};
        sorted_tasks.forEach((t, index) => {
            switch (filters.group_type) {
                case 'days':
                    const key = moment(t.start_at).format('dddd DD MMMM');
                    if (!keys_map[key])
                        keys_map[key] = [];

                    keys_map[key].push(t);
                    break;
                case 'screenings':
                    const screening = _.find(screenings, (s) => s.id === t.screening_id);
                    if (screening) {
                        const key = Utils.localized_or_fallback(screening.title, default_lang) + ' / ' + moment(screening.start_at).format('dddd DD MMMM HH:mm') + " - " + screening?.place?.name ?? "";
                        if (!keys_map[key])
                            keys_map[key] = [];

                        keys_map[key].push(t);
                    }
                    break;
                case 'shifts':
                    const shift = _.find(shifts, (s) => s.id === t.shift_id);
                    if (shift) {
                        const key = moment(shift.start_at).format('dddd DD MMMM HH:mm') + " - " + Utils.localized_or_fallback(shift.sector?.name, default_lang);
                        if (!keys_map[key])
                            keys_map[key] = [];

                        keys_map[key].push(t);
                    }
                    break;
                case 'sectors':
                    const sector = _.find(sectors, (s) => s.id === t.shift?.sector_id);
                    if (sector) {
                        const key = Utils.localized_or_fallback(sector?.name, default_lang);
                        if (!keys_map[key])
                            keys_map[key] = [];

                        keys_map[key].push(t);
                    }
                    break;
            }
        });

        return keys_map;
    }, [ tasks, sorted_tasks, filters?.group_type, shifts, sectors ]);

    const screenings_map = React.useMemo(() => {
        if (filters.group_type !== 'screenings')
            return {};

        const screenings_map = {};
        sorted_tasks.forEach((t, index) => {
            const screening = _.find(screenings, (s) => s.id === t.screening_id);
            if (screening) {
                const key = Utils.localized_or_fallback(screening.title, default_lang) + ' / ' + moment(screening.start_at).format('dddd DD MMMM HH:mm') + " - " + screening?.place?.name ?? "";
                if (!screenings_map[key])
                    screenings_map[key] = [];

                screenings_map[key] = screening;
            }
        });

        return screenings_map;
    }, [ sorted_tasks, filters?.group_type ]);

    const textToCopy = React.useMemo(() => {
        return sorted_tasks?.map((task) => {
            const parts = [
                moment(task.start_at).format('LLL'),
                moment(task.stop_at).format('LLL'),
                task?.user?.fullname ?? "",
                distinctActivities?.length > 1 && task.activity ? Utils.localized_or_fallback(task.activity.name, default_lang) : "",
                task.shift?.sector ? Utils.localized_or_fallback(task.shift.sector.name, default_lang) : "",
                task.shift?.sector?.admins?.length > 0 ? `${t('tasks.admins')} ${ task.shift.sector.admins?.map(admin => admin.fullname).join(' / ') }` : ""
            ].filter(v => v?.length);
            return parts.join(' - ');
        }).join('\r\n')

    }, [sorted_tasks]);

    const downloadLink = React.useMemo(() => {
        const link = new URL(`${Config.api_end_point}/task/xlsx`);

        link.searchParams.append('x-api-key', user.api_key);
        link.searchParams.append('lang', i18n.language);

        if (!filters)
            return link.href;

        ['activity_id', 'period_id', 'phase_id', 'screening_id', 'sector_id', 'status', 'user_id'].forEach(property => {
            if (filters[property] && filters[property].length)
                link.searchParams.append(property, filters[property]);
        });
        if (filters.start_at_gte)
            link.searchParams.append('start_at_gte', filters.start_at_gte.toISOString());
        if (filters.start_at_lte)
            link.searchParams.append('start_at_lte', filters.start_at_lte.toISOString());


        return link.href;
    }, [filters], i18n.language, user);


    const buildTaskUserOptions = task => {
        if (!currentTask)
            return [];

        const toJsxOption = (option, key) => (
            <option key={key} id={option.value} value={option.value} disabled={option.disabled}>
                {option.text}
            </option>
        );

        const options = [{ value: '', text: '---'}];
        if (currentTask.user)
            options.push({ value: currentTask.user.id, text: currentTask.user.fullname });

        if (!currentTask.shift) {
            if (currentTask.user)
                options.push({ value: '', text: '---' });
            (currentTask.suggestions || []).forEach(u => options.push({
                value: u.id,
                text: u.fullname
            }));

            return options.map(toJsxOption);
        }

        const groups = [];

        const available = currentTask.suggestions.filter(
            suggestion => !!suggestion.in_sector && !suggestion.overbooked
        );
        available?.length && groups.push({
            text: t('shifts.available'),
            options: available.map(u => ({ value: u.id, text: u.fullname }))
        });

        const outOfSector = currentTask.suggestions.filter(
            suggestion => !suggestion.in_sector && !suggestion.overbooked
        );
        outOfSector?.length && groups.push({
            text: t('shifts.out_of_sector'),
            options: outOfSector.map(u => ({ value: u.id, text: u.fullname }))
        });

        const overbooked = currentTask.suggestions.filter(
            suggestion => !!suggestion.overbooked
        );
        overbooked?.length && groups.push({
            text: t('shifts.overbooked'),
            options: overbooked.map(u => ({ value: u.id, text: u.fullname, disabled: true }))
        });

        return (
            <>
                { options.map(toJsxOption)  }
                { groups.map(group => (
                    <optgroup label={group.text}>
                        {group.options.map(toJsxOption)}
                    </optgroup>
                ))}
            </>
        );
    };

    const renderTask = task => {
        return (
            <li key={task.id} className={`task_${task.status}`}>
                <div className="flex-1 d-flex justify-start gap-2">
                    <span className="activity">
                        {distinctActivities?.length > 1 && task.activity && (
                            <>
                                {Utils.localized_or_fallback(task.activity.name, default_lang)}&nbsp;
                            </>
                        )}
                        {(filters.group_type !== 'shifts') && (filters.group_type !== 'sectors') && (!filters.sector_id) && (task.shift?.sector) && (
                            <span className="sector">
                                { Utils.localized_or_fallback(task.shift.sector.name, default_lang) }
                            </span>
                        )}
                        {task.comment &&
                            <i className="fa fa-comments">&nbsp;</i>
                        }
                    </span>
                    <span className="when">
                        <i className="fa fa-clock-o"></i>&nbsp;
                        {moment(task.start_at).format('HH:mm') + ' - ' + moment(task.stop_at).format('HH:mm')}
                    </span>

                    {task.is_assignable && me?.can('change_status', 'tasks') ? (
                        <span className="status editable">
                            {task.id === nextCurrentTaskId && isLoadingTask ? (
                                <Loader raw size="20px" />
                            ) : isEditing(task) && (nextCurrentTaskId === task.id) ? (
                                <InputGroup size="sm">
                                    <InputGroupAddon addonType="prepend" className="noPrint">{t('tasks.status')}</InputGroupAddon>
                                    <Input id={`status-select-${task.id}`} type="select" className="custom-select" value={task.status || ""} onChange={(e) => dispatch(changeTaskStatus(task.id, e.target.value))}>
                                        <option key="0" value="">---</option>
                                        {[Utils.TASK_STATUS_NEW, Utils.TASK_STATUS_ASSIGNED, Utils.TASK_STATUS_FREE].map((s) =>
                                            <option key={s} value={s} >{t('tasks.status_' + s)}</option>
                                        )}
                                    </Input>
                                </InputGroup>
                            ) : (
                                <Button size="xs" color="dark" onClick={e => onTaskClick('status', task)} className="w-100">
                                    <i className="fa fa-edit"></i>&nbsp;
                                    {t('tasks.status_' + task.status)}
                                </Button>
                            )}
                        </span>
                    ) : (
                        <span className="status">
                            {t('tasks.status_' + task.status)}
                        </span>
                    )}


                    {task.is_assignable && me?.can('assign', 'tasks') ?
                        <span className="user assign">
                            {task.id === nextCurrentTaskId && isLoadingTask ? (
                                <div className="text-center">
                                    <Loader raw size="20px" />
                                </div>
                            ) : isEditing(task) && (nextCurrentTaskId === task.id) ? (
                                <InputGroup size="sm">
                                    <InputGroupAddon addonType="prepend" className="noPrint">{t('tasks.assign')}</InputGroupAddon>
                                    <Input id={`user-select-${task.id}`} type="select" className="custom-select" value={task.user_id || ""} onChange={(e) => dispatch(assignTask(task.id, e.target.value))}>
                                        { buildTaskUserOptions(task) }
                                    </Input>
                                </InputGroup>
                            ) : (
                                <Button size="xs" color="dark" className="w-100" onClick={e => onTaskClick('user', task)}>
                                    <i className="fa fa-user"></i>&nbsp;
                                    {task?.user?.fullname ?? "-"}
                                </Button>
                            )}
                        </span>
                        :
                        <span className="user">
                            <>
                                <i className="fa fa-user"></i>&nbsp;
                                {task?.user?.fullname ?? "-"}
                            </>
                        </span>
                    }

                    {isEditing(task) && (nextCurrentTaskId === task.id) && (
                        <Button onClick={resetEdition} className="text-white" color="link" size="xs">
                            {t('common.close')}
                        </Button>
                    )}

                    {task.is_editable &&
                        <ButtonGroup className="buttons">
                            <TableButtons
                                id={task.id}
                                actions={["edit", "delete"]}
                                module="tasks"
                                owner={task.created_by}
                            />
                            {task.is_renouncable && me?.can('renounce', 'tasks') &&
                                <Button onClick={(e) => showRenounceModal(task)} color="warning" title={t("tasks.renounce")} size="sm">
                                    <i className="fa fa-thumbs-down"></i>&nbsp;
                                    <span>{t("tasks.renounce")}</span>
                                </Button>
                            }
                            {task.is_claimable && me?.can('claim', 'tasks') &&
                                <Button onClick={(e) => dispatch(claimTask(task.id))} color="info" title={t("tasks.claim")} size="sm">
                                    <i className="fa fa-hand-rock-o"></i>&nbsp;
                                    <span>{t("tasks.claim")}</span>
                                </Button>
                            }
                        </ButtonGroup>
                    }
                </div>

                {(!filters.sector_id) && (task.shift?.sector?.admins?.length > 0) && (
                    <span className="admins d-flex justify-end gap-2">
                        { task.shift.sector.admins?.map(admin => (
                            <div className="badge badge-light ml-2">
                                { admin.mobile_phone?.length ?
                                    <a href={Utils.getPhoneNumberUri(admin.mobile_phone)}>
                                        {admin.fullname}
                                        <div className="small">
                                            {Utils.formatPhoneNumber(admin.mobile_phone)}
                                        </div>
                                    </a> :
                                    admin.fullname
                                }
                            </div>
                        ))}
                    </span>
                )}
            </li>
        );
    };

    const group_resume = group => {
        const total      = group?.length || 0;
        const fullfilled = group?.filter(t => !!t.user_id)?.length || 0;

        if (total === fullfilled)
            return t('tasks.fullfilled_tasks', { fullfilled });

        return t('tasks.nb_tasks', { total, fullfilled });
    };

    const nb_renounced = React.useMemo(() => {
        return tasks?.filter(t => t.status === Utils.TASK_STATUS_RENOUNCED)?.length || 0;
    }, [tasks]);

    return (
        <div className="TasksListDetails">
            <RenounceModal
                task={taskToRenounce}
                isOpen={isOpenRenounceModal}
                onClose={closeRenounceModal}
                onSubmit={submitRenounceModal}
            />

            <div className="noPrint clearfix">
                <div className={isMobile || isTablet ? '' : 'pull-right'}>
                    <ButtonGroup>
                        <CopyButton textToCopy={ textToCopy } title={ t('tasks.copy_to_clipboard') } />
                        <CopyButton
                            textToCopy={ Tasks.icsLink(filters) }
                            color="primary"
                            title={ t('tasks.ics_link') }
                            onCopy={() => showIcsDesc(true)}
                        />
                    </ButtonGroup>
                    <a target="_blank" href={ downloadLink } className="btn btn-info btn-xs ml-3" >
                        <i className="fa fa-download"></i>&nbsp;{t("tasks.export_xlsx")}
                    </a>
                </div>
                {icsDescIsVisible && (
                    <div className="d-flex flex-column w-100 m-2 p-2 border text-center">
                        { t('tasks.ics_link_description') }<br />
                        { t('tasks.ics_link_description_2') }
                        <Button onClick={() => showIcsDesc(false)} color="success" size="xs" className="mt-1">
                            <i className="fa fa-check" />&nbsp;
                            { t('tasks.ics_link_i_got_it') }
                        </Button>
                    </div>
                )}
            </div>

            <ul className="tasks-ul">
                {tasks?.length === TASKS_LIMIT && (
                    <h4>
                        <i className="fa fa-warning" />&nbsp;
                        { t('tasks.limit_was_reached', {nb: TASKS_LIMIT}) }
                    </h4>
                )}
                {nb_renounced > 0 && (
                    <h4>
                        <i className="fa fa-warning" />&nbsp;
                        { t('tasks.nb_renounced_tasks', {nb: nb_renounced}) }
                    </h4>
                )}
                {Object.keys(keys_map)?.map((k) => (
                    <li key={k}>
                        <h3>
                            {filters.group_type === 'days' &&
                                <i className="fa fa-calendar-o"></i>
                            }
                            {filters.group_type === 'screenings' &&
                                <i className="fa fa-th"></i>
                            }
                            {filters.group_type === 'shifts' &&
                                <i className="fa fa-calendar-plus-o"></i>
                            }
                            &nbsp;{k} - {group_resume(keys_map[k])}
                            {filters.group_type === 'screenings' && (
                                <small className="d-flex justify-start gap-1">
                                    { screenings_map[k]?.version && (
                                        <Badge >{ screenings_map[k]?.version }</Badge>
                                    )}
                                    { screenings_map[k]?._3d && (
                                        <Badge color="danger">3D</Badge>
                                    )}
                                    { screenings_map[k]?.break && (
                                        <Badge color="info">
                                            <i className="fa fa-pause" />
                                        </Badge>
                                    )}
                                </small>
                            )}
                        </h3>
                        <ul> { keys_map[k].map(renderTask) } </ul>
                    </li>
                ))}
            </ul>
        </div>
    )
}

export default TasksListDetails;
